import { useRef } from 'react'
import PanelTab from '@/components/panel-tab/PanelTab'
import clsx from 'clsx'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import { CollapseAlt, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'

export default function TableSourceTabs({
  title,
  sourceVisible,
  onSelect,
  onClose,
  onCollapse,
}: {
  title?: string
  sourceVisible: boolean
  onSelect: (source: boolean) => void
  onClose: () => void
  onCollapse?: () => void
}) {
  const chatRef = useRef<HTMLButtonElement>(null)
  const sourceRef = useRef<HTMLButtonElement>(null)
  const closeSource = () => {
    onClose()
    chatRef.current?.focus()
  }
  const selectWithKeyboard = (key: string) => {
    if (!title || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return false
    const source = key === 'Home' ? false : key === 'End' ? true : !sourceVisible
    onSelect(source)
    ;(source ? sourceRef : chatRef).current?.focus()
    return true
  }
  return (
    <div className="flex h-9 shrink-0 items-start gap-1 border-b border-brand-neutral-300 bg-brand-neutral-200 px-1 pt-1">
      {onCollapse && <IconButton icon={<CollapseAlt />} size="sm" variant="tonal" className="shrink-0" aria-label="Close sidebar" onClick={onCollapse} />}
      <div className="relative flex min-w-0">
        <div
          role="tablist"
          aria-label="Table chat and source"
          className="flex min-w-0 flex-1 gap-1"
          onKeyDown={(event) => {
            if (selectWithKeyboard(event.key)) event.preventDefault()
          }}
        >
          <PanelTab
            label="AI Assistant"
            icon={<img src="/images/logos/paxton-logo-svg.svg" alt="" className="size-5 shrink-0" />}
            id="table-chat-tab"
            controlsId="table-chat-content"
            selected={!sourceVisible}
            onSelect={() => onSelect(false)}
            tabRef={chatRef}
            size="sm"
            className="shrink-0"
          />
          {title && (
            <button
              type="button"
              role="tab"
              id="table-source-tab"
              aria-controls="table-source-content"
              aria-selected={sourceVisible}
              tabIndex={sourceVisible ? 0 : -1}
              ref={sourceRef}
              onClick={() => onSelect(true)}
              onKeyDown={(event) => {
                if (event.key === 'Delete') {
                  event.preventDefault()
                  closeSource()
                }
              }}
              // Reserve the same 28px close control, 1px gap, and 2px inset as PanelTab.
              className={clsx(
                '-mb-px flex h-8 min-w-0 items-center rounded-t-lg border border-brand-neutral-300 pl-2 pr-[31px] text-xs leading-4',
                sourceVisible ? 'border-b-brand-neutral-50 bg-brand-neutral-50 cursor-default' : 'bg-brand-neutral-100 hover:bg-brand-neutral-200'
              )}
            >
              <TruncatedText
                placement="bottom-start"
                className={sourceVisible ? 'font-semibold text-brand-neutral-950' : 'font-medium text-true-gray-700'}
                disableOnTouch
              >
                {title}
              </TruncatedText>
            </button>
          )}
        </div>
        {title && (
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            icon={<XClose />}
            aria-label={`Close ${title}`}
            onClick={closeSource}
            tabIndex={sourceVisible ? 0 : -1}
            wrapperClassName="absolute right-[3px] top-0.5"
            className={sourceVisible ? undefined : 'hover:!bg-brand-neutral-300'}
          />
        )}
      </div>
    </div>
  )
}
